import { camelCase } from "lodash-es";
import { Descendant, Element, Text } from "slate";

import { ImageElement, ParagraphElement, ResourceLinkElement } from "../../../type/slate-types";
import ElementAlignment from "../../../type/slate/ElementAlignment";
import ElementType from "../../../type/slate/ElementType";

import { RESOURCE_ID_DATA_ATTRIBUTE } from "./const";
import parseTextNode, { isTextNode, parseTextChildNode } from "./parseTextNode";

export const RESOURCE_ID_DATA_ATTRIBUTE_NAME = camelCase(RESOURCE_ID_DATA_ATTRIBUTE);

const getElementsStyleValue = (
  elements: HTMLElement[],
  styleName: keyof CSSStyleDeclaration
): string | undefined => {
  const element = elements.find((element) => !!element.style[styleName]);
  if (!element) {
    return undefined;
  }
  return String(element.style[styleName]);
};

const parseChildNodes = (childNodes: NodeListOf<ChildNode>): Descendant[] => {
  return Array.from(childNodes)
    .map((childNode) => {
      // A text node child — keep its content unless it's pure line-wrapping whitespace.
      if (childNode.nodeType === Node.TEXT_NODE) {
        const text = childNode.textContent ?? "";
        // A whitespace run that wraps a line is formatting: composedBody appends its blocks as
        // indented template literals. A space with no line break separates two inline runs.
        return text.trim().length === 0 && text.includes("\n") ? [] : parseTextChildNode(childNode);
      }
      if (childNode.nodeType !== Node.ELEMENT_NODE) {
        return [];
      }
      const element = childNode as HTMLElement;
      return (
        parseResourceNode(element) ??
        parseImageWithLinkNode(element) ??
        parseMobileLinkNode(element) ??
        (isTextNode(element) ? parseTextNode(element) : parseElementNode(element))
      );
    })
    .flat();
};
const parseElementNode = (element: HTMLElement): Element => {
  const children = parseChildNodes(element.childNodes);

  const alignValue = getElementsStyleValue([element], "textAlign");
  const marginLeftValue = getElementsStyleValue([element], "marginLeft");
  const tagName = element.tagName.toLowerCase();

  const srcAttribute = element.getAttribute("src");
  const altAttribute = element.getAttribute("alt");
  const widthAttribute = element.getAttribute("width");
  const hrefAttribute = element.getAttribute("href");

  if (children.length === 0) {
    // mobile application can insert links without additional nodes inside
    children.push({ text: tagName === "a" ? element.innerText : "" });
  }

  return {
    type:
      tagName === "a"
        ? ElementType.LINK
        : tagName === "ul"
          ? ElementType.BULLETED_LIST
          : tagName === "ol"
            ? ElementType.NUMBERED_LIST
            : tagName === "li"
              ? ElementType.LIST_ITEM
              : tagName === "img"
                ? ElementType.IMAGE
                : ElementType.PARAGRAPH,
    ...(alignValue
      ? {
          align:
            alignValue === "left"
              ? ElementAlignment.ALIGN_LEFT
              : alignValue === "center"
                ? ElementAlignment.ALIGN_CENTER
                : alignValue === "right"
                  ? ElementAlignment.ALIGN_RIGHT
                  : ElementAlignment.ALIGN_JUSTIFY,
        }
      : {}),
    ...(marginLeftValue
      ? {
          indent: parseInt(marginLeftValue.replace("rem", ""), 10),
        }
      : {}),
    ...(srcAttribute ? { src: srcAttribute } : {}),
    ...(altAttribute ? { alt: altAttribute } : {}),
    ...(hrefAttribute ? { url: hrefAttribute } : {}),
    ...(widthAttribute ? { width: parseInt(widthAttribute.replace("px", "")) } : {}),
    children,
  } as Element;
};

const parseImageWithLinkNode = (element: HTMLElement) => {
  if (
    element.tagName.toLowerCase() === "a" &&
    element.children.length === 1 &&
    element.children[0].tagName.toLowerCase() === "img"
  ) {
    const imageElement = parseElementNode(element.children[0] as HTMLElement) as ImageElement;

    const url = element.getAttribute("href");
    if (url) {
      imageElement.url = url;
    }

    return imageElement;
  }
};

// mobile application insert links wrapped into span when link text does not have any formatting
// when link text has formatting mobile insert links in the same way with web
const parseMobileLinkNode = (element: HTMLElement) => {
  if (
    element.tagName.toLowerCase() === "span" &&
    element.children.length === 1 &&
    element.children[0].tagName.toLowerCase() === "a"
  ) {
    return parseElementNode(element.children[0] as HTMLElement);
  }
};

const parseResourceNode = (element: HTMLElement) => {
  if (element.tagName.toLowerCase() === "div") {
    const resourceId = parseInt(element.dataset[RESOURCE_ID_DATA_ATTRIBUTE_NAME] || "", 10) || 0;
    if (resourceId) {
      return {
        children: [{ text: "" }],
        resourceId,
        type: ElementType.RESOURCE_LINK,
      } as ResourceLinkElement;
    }
  }
};

const INLINE_ELEMENT_TYPES: ElementType[] = [
  ElementType.DYNAMIC_VARIABLE,
  ElementType.IMAGE,
  ElementType.LINK,
];

const isRootLevelBlock = (descendant: Descendant): boolean =>
  Element.isElement(descendant) && !INLINE_ELEMENT_TYPES.includes(descendant.type);

const isBlankText = (descendant: Descendant): boolean =>
  Text.isText(descendant) && descendant.text.trim().length === 0;

const toParagraph = (children: Descendant[]): ParagraphElement => ({
  children,
  type: ElementType.PARAGRAPH,
});

// Slate deletes text and inline children of the document instead of wrapping them, so a body with
// no block markup — bare text, a merge tag, a link — has to reach the root already in a paragraph.
const wrapRootLevelInlines = (descendants: Descendant[]): Descendant[] => {
  const result: Descendant[] = [];
  let inlineRun: Descendant[] = [];

  const flushInlineRun = () => {
    // whitespace between two blocks is layout, not content
    if (inlineRun.some((descendant) => !isBlankText(descendant))) {
      result.push(toParagraph(inlineRun));
    }
    inlineRun = [];
  };

  descendants.forEach((descendant) => {
    // originally image was a block element and refactored to inline later
    // in slate inline elements cannot be direct children of document
    // so to keep backward compatibility we wrap images into paragraph elements
    if (Element.isElement(descendant) && descendant.type === ElementType.IMAGE) {
      flushInlineRun();
      result.push(toParagraph([descendant]));
    } else if (isRootLevelBlock(descendant)) {
      flushInlineRun();
      result.push(descendant);
    } else {
      inlineRun.push(descendant);
    }
  });
  flushInlineRun();

  return result;
};

const deserializeFromHTML = (html: string): Descendant[] => {
  const mainContainer = new window.DOMParser().parseFromString(html, "text/html");

  const result = wrapRootLevelInlines(parseChildNodes(mainContainer.body.childNodes));

  if (result.length === 0) {
    result.push(toParagraph([{ text: "" }]));
  }

  return result;
};

export default deserializeFromHTML;
